import * as React from 'react';
import { ProductType, Product } from "app/shared/model";
import { ProductFilterType, ProductFilter, getProductType, setProductFilterType, setProductType } from "app/shared/model/ProductFilter";
import { ProductFilterAllowedOptions } from 'app/pages/DiscountEditor/components/ProductSelection/ProductFilterAllowedOptions';
import { Select } from 'antd';

type SelectionOption = {
  text: string;
  value: string;
  key: string;
};

interface SelectionManager {
  readonly id: string;
  readonly caption: string;
  matches(filter: ProductFilter): boolean;
  updateFilter(filter: ProductFilter): ProductFilter;
  includeFor(filter: ProductFilter, allowed: ProductFilterAllowedOptions): boolean;
}

class FilterTypeSelectionManager implements SelectionManager {
  id: string;
  caption: string;
  private productFilterType: ProductFilterType;

  constructor(caption: string, productFilterType: ProductFilterType) {
    this.caption = caption;
    this.id = 'FT.' + productFilterType;
    this.productFilterType = productFilterType;
  }

  matches(filter: ProductFilter) {
    if (this.productFilterType === ProductFilterType.Homestay || this.productFilterType === ProductFilterType.Residence) {
      if (filter.type === this.productFilterType) {
        return true;
      }
      if (filter.type === ProductFilterType.IncludeSpecific && filter.productType === ProductType.Accommodation) {
        return false;
      }
    }
    return filter.type === this.productFilterType;
  }

  updateFilter(filter: ProductFilter) {
    return setProductFilterType(filter, this.productFilterType);
  }

  includeFor(filter: ProductFilter, allowed: ProductFilterAllowedOptions) {
    switch (allowed) {
      case ProductFilterAllowedOptions.DisallowAnyProduct:
        return this.productFilterType !== ProductFilterType.AnyProduct;
      case ProductFilterAllowedOptions.DisallowProductTypeChange:
        const pt = getProductType(filter);
        if (pt === undefined) return true;
        if (pt !== ProductType.Accommodation) return false;
        return (
          this.productFilterType === ProductFilterType.Homestay ||
          this.productFilterType === ProductFilterType.Residence
        );
      default:
        return true;
    }
  }
}

class ProductTypeSelectionManager implements SelectionManager {
  id: string;
  caption: string;
  private productType: ProductType;

  constructor(caption: string, productType: ProductType) {
    this.caption = caption;
    this.id = 'PT.' + productType;
    this.productType = productType;
  }

  matches(filter: ProductFilter) {
    switch (filter.type) {
      case ProductFilterType.ByType:
      case ProductFilterType.IncludeSpecific:
      case ProductFilterType.ExcludeSpecific:
        return filter.productType === this.productType;
      default:
        return false;
    }
  }

  updateFilter(filter: ProductFilter) {
    return setProductType(filter, this.productType);
  }

  includeFor(filter: ProductFilter, allowed: ProductFilterAllowedOptions) {
    if (allowed !== ProductFilterAllowedOptions.DisallowProductTypeChange) {
      return true;
    }
    const pt = getProductType(filter);
    return pt === undefined || pt === this.productType;
  }
}

function filterOption(
  caption: string,
  type: ProductFilterType
): SelectionManager {
  return new FilterTypeSelectionManager(caption, type);
}

function typeOption(
  caption: string,
  pt: ProductType
): SelectionManager {
  return new ProductTypeSelectionManager(caption, pt);
}

const SelectionOptions: SelectionManager[] = [
  filterOption('any product', ProductFilterType.AnyProduct),
  typeOption('a course', ProductType.Course),
  typeOption('accommodation', ProductType.Accommodation),
  filterOption('any residence', ProductFilterType.Residence),
  filterOption('any homestay', ProductFilterType.Homestay),
  typeOption('an add-on', ProductType.AddOn),
  typeOption('a transfer', ProductType.Transfer)
];

const SelectionMode: SelectionManager[] = [
  filterOption('of any type', ProductFilterType.ByType),
  filterOption('selected below', ProductFilterType.IncludeSpecific),
  filterOption('not selected below', ProductFilterType.ExcludeSpecific)
];

function selectionOptionValue(props: {
  productFilter: ProductFilter;
}): string {
  const found =
    SelectionOptions.find(o => o.matches(props.productFilter)) ||
    SelectionOptions[0];
  return found.id;
}

function getSelectionModeOptions(
  filter: ProductFilter,
  allowed: ProductFilterAllowedOptions = ProductFilterAllowedOptions.All
): SelectionOption[] {
  return SelectionOptions
    .filter(o => o.includeFor(filter, allowed))
    .map(o => ({ key: o.id, value: o.id, text: o.caption }));
}

function changeProductFilterType(
  props: {
    productFilter: ProductFilter;
    changeProductFilter: (f: ProductFilter) => void;
  },
  option: { value: string }
) {
  const selected = SelectionOptions.find(o => o.id === option.value);
  if (!selected) return;
  const nf = selected.updateFilter(props.productFilter);
  if (nf !== props.productFilter) props.changeProductFilter(nf);
}

function changeProductFilterMode(
  props: {
    productFilter: ProductFilter;
    changeProductFilter: (f: ProductFilter) => void;
  },
  value: string
) {
  const selected = SelectionMode.find(o => o.id === value);
  if (!selected) return;
  const nf = selected.updateFilter(props.productFilter);
  if (nf !== props.productFilter) props.changeProductFilter(nf);
}

interface SelectionOptionEditorProps {
  productFilter: ProductFilter;
  allowedFilterOptions: ProductFilterAllowedOptions;
  changeProductFilter: (f: ProductFilter) => void;
}

export function SelectionOptionEditor(
  props: SelectionOptionEditorProps
) {
  const mode = SelectionMode.find(o => o.matches(props.productFilter));

  const modeOpts = SelectionMode.map(o => ({
    key: o.id,
    value: o.id,
    text: o.caption
  }));

  return (
    <div className="sentence-dropdown">
      <span>The discount will apply if the booking has </span>
      {(() => {
        const opts = getSelectionModeOptions(
          props.productFilter,
          props.allowedFilterOptions
        );
        if (opts.length === 0) return <span>An item</span>;
        if (opts.length === 1) return <span>{opts[0].text}</span>;
        return (
          <Select
            size="small"
            style={{ minWidth: 120 }}
            options={opts.map(o => ({ label: o.text, value: o.value, key: o.key }))}
            value={selectionOptionValue(props)}
            onChange={(value: any) =>
              changeProductFilterType(props, { value })
            }
          />
        );
      })()}
      {mode && (
        <>
          <span> that is </span>
          <Select
            size="small"
            style={{ minWidth: 120 }}
            options={modeOpts.map(o => ({ label: o.text, value: o.value, key: o.key }))}
            value={mode.id}
            onChange={(value: string) =>
              changeProductFilterMode(props, value)
            }
          />
        </>
      )}
    </div>
  );
}

interface SelectionOptionDisplayProps {
  productFilter: ProductFilter;
}

export function SelectionOptionDisplay(
  props: SelectionOptionDisplayProps
) {
  const option = SelectionOptions.find(o => o.matches(props.productFilter));
  const mode = SelectionMode.find(o => o.matches(props.productFilter));

  return (
    <p>
      <span>The discount will apply if the booking has </span>
      <span>{option?.caption || 'unknown'}</span>
      {mode && <span> that is {mode.caption}</span>}
    </p>
  );
}